<template>
    <v-container>
        <h1 class="titulo mb-3">CONFIGURAÇÃO DE LIMITE DE SALDO DEVEDOR POR TIPO DE GRUPO</h1>
        <v-card class="card">
            <v-card-text>
                <m-form ref="form">
                    <v-row>
                        <v-col cols="12" md="5">
                            <MoneyTextField
                                label="Limite de Saldo Devedor:"
                                placeholder="Indique o valor do limite de saldo devedor"
                                outlined
                                class="required"
                                :rules="[$rules.required]"
                                v-model="configuracao.limite_saldo_devedor"
                                maxvalue="999999999999.99"
                            />
                        </v-col>
                        <v-col cols="12" md="5">
                            <Autocomplete
                                label="Tipo do Grupo: *"
                                placeholder="Selecione o(s) tipo(s) de grupo(s)"
                                :items="tiposGrupo"
                                item-text="label"
                                item-value="value"
                                v-model="configuracao.tipo_grupo"
                                :rules="[$rules.required]"
                                outlined
                                multiple
                                chips
                                deletable-chips
                            />
                        </v-col>
                        <v-col cols="12" md="2" class="d-flex align-center justify-center">
                            <v-btn class="transition" color="primary" rounded @click="inserir">
                                SALVAR
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="8">
                            <HintMessage
                                style="max-width: 550px;"
                                class="mb-5"
                                title="DICA PARA CONFIGURAÇÃO"
                                icon="mdi-lightbulb-on-outline"
                                icon-color="secondary"
                                description='Em "Tipo do Grupo", <strong>você poderá selecionar um ou mais de um.</strong>'
                                :description-html="true"
                                :center-icon="false"
                            />
                        </v-col>
                    </v-row>
                </m-form>

                <v-layout class="d-flex justify-space-between flex-wrap">
                    <v-btn class="transition ml-1" color="primary" rounded @click="toggleFiltro">
                        <v-icon small style="margin-right: 10px;">rod-filter</v-icon>
                        FILTRAR POR
                        <v-icon :class="{ 'rotate90': filter_open }" small>chevron_right</v-icon>
                    </v-btn>
                    <v-btn class="transition mr-1" color="primary" rounded @click="exportarExcel">
                        <v-icon left size="18">mdi-file-chart</v-icon>
                        GERAR RELATÓRIO
                    </v-btn>
                </v-layout>

                <collapse-transition>
                    <m-form ref="filter" v-show="filter_open" class="mt-4 ml-2">
                        <v-row>
                            <v-col cols="12" md="6">
                                <MoneyTextField
                                    label="Limite de Saldo Devedor"
                                    placeholder="Indique o valor do limite de saldo devedor"
                                    outlined
                                    v-model="filters.limite_saldo_devedor"
                                    maxvalue="999999999999.99"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <Autocomplete
                                    label="Tipo do Grupo"
                                    placeholder="Selecione o(s) tipo(s) de grupo(s)"
                                    :items="tiposGrupo"
                                    item-text="label"
                                    item-value="value"
                                    v-model="filters.tipo_grupo"
                                    outlined
                                    multiple
                                    chips
                                    deletable-chips
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6" class="d-flex justify-start">
                                <v-btn color="secondary" @click="limpar">Limpar</v-btn>
                            </v-col>
                            <v-col cols="6" class="d-flex justify-end">
                                <v-btn color="primary" @click="filtrar">Filtrar</v-btn>
                            </v-col>
                        </v-row>
                    </m-form>
                </collapse-transition>

                <DataTable
                    ref="datatable"
                    url="/painel/config_limite_saldo_devedor/listar"
                    class="pt-2 data-table-overlow"
                    :headers="headers"
                    :params="params"
                    :filter="dtFilters"
                >
                    <template v-slot:items="{ item }">
                        <td class="text-center">
                            <v-menu :close-on-content-click="false">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        color="primary"
                                        class="btn-acoes"
                                        v-bind="attrs"
                                        v-on="on"
                                        style="margin: 10px 0px; padding: 5px; min-width: 48px;"
                                    >
                                        <v-icon>mdi-dots-horizontal</v-icon>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item>
                                        <v-list-item-title class="font-weight-bold">
                                            O que deseja fazer?
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="editar(item)">
                                        <v-list-item-title>Editar</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="confirmExcluir(item.id)">
                                        <v-list-item-title>Excluir</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </td>
                        <td class="text-center">{{ item.limite_saldo_devedor | money }}</td>
                        <td class="text-center">
                            <v-chip
                                v-for="(label, index) in item.tipo_grupo_labels"
                                :key="index"
                                small
                                class="mr-1"
                                color="grey lighten-3"
                            >
                                {{ label }}
                            </v-chip>
                        </td>
                    </template>
                </DataTable>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
  import { AlertTypes } from '@/plugins/alerts_types'
  import { alert, notification } from '@/plugins/helpers'
  import { TiposGrupoFiltro } from '@/plugins/constants'
  import * as Qs from 'qs'
import { money } from '@/plugins/formatters';

  export default {
    name: 'ConfigCreditAnalysisLimiteSaldoDevedor',
    data: () => ({
      filter_open: false,
      pagination: {
        sortBy: [], descending: []
      },
      dtFilters: {},
      tiposGrupo: TiposGrupoFiltro,
      configuracao: {
        id: null,
        limite_saldo_devedor: null,
        tipo_grupo: []
      },
      filters: {
        limite_saldo_devedor: null,
        tipo_grupo: []
      },
      headers: [
        { text: 'Ações', value: '', align: 'center', sortable: false },
        { text: 'Limite de Saldo Devedor', value: 'limite_saldo_devedor', align: 'center', sortable: true },
        { text: 'Tipo do Grupo', value: 'tipo_grupo_labels', align: 'center', sortable: false }
      ]
    }),
    computed: {
      params () {
        return {
          pagination: this.pagination
        }
      }
    },
    methods: {
      async inserir () {
        if (!this.$refs.form.validate()) {
          return
        }

        const payload = {
          limite_saldo_devedor: parseFloat(this.configuracao.limite_saldo_devedor),
          tipo_grupo: this.configuracao.tipo_grupo
        }

        if (this.configuracao.id) {
          await this.salvar(payload, this.configuracao.id)
        } else {
          await this.salvar(payload)
        }
      },

      async salvar (payload, id = null) {
        try {
          let response
          if (id) {
            response = await this.$http.put(`/painel/config_limite_saldo_devedor/editar/${id}`, payload)
          } else {
            response = await this.$http.post('/painel/config_limite_saldo_devedor/salvar', payload)
          }

          if (response.data.success) {
            notification(AlertTypes.SUCCESS, response.data.message)
          }

          this.resetForm()
          this.$refs.datatable.reload()
        } catch (error) {
          const errorMessage = error.response?.data?.error || error.message
          notification(AlertTypes.ERROR, errorMessage)
        }
      },

      editar (item) {
        this.configuracao = {
          id: item.id,
          limite_saldo_devedor: item.limite_saldo_devedor,
          tipo_grupo: item.tipo_grupo_labels
            ? item.tipo_grupo_labels.map(label => {
                const found = TiposGrupoFiltro.find(t => t.label.toUpperCase() === label.toUpperCase())
                return found ? found.value : label
              })
            : []
        }
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },

      async confirmExcluir (id) {
        alert({
          text: 'Tem certeza que deseja excluir a configuração de limite de saldo devedor?',
          buttons: {
            'Cancelar': () => true,
            'Confirmar': async () => {
              try {
                const response = await this.$http.delete(`/painel/config_limite_saldo_devedor/excluir/${id}`)
                notification(AlertTypes.SUCCESS, response.data.message)
                this.$refs.datatable.reload()
              } catch (error) {
                const errorMessage = error.response?.data?.error || error.message
                notification(AlertTypes.ERROR, errorMessage)
              }
            }
          }
        })
      },

      exportarExcel () {
        const filtros = { ...this.filters }

        const encoded_params = Qs.stringify({
          filter: filtros,
          pagination: {
            sortBy: this.params.pagination.sortBy,
            descending: this.params.pagination.descending
          },
          headers: this.headers.map(header => header.value),
          headers_formatado: this.headers.map(header => header.text)
        }, {
          arrayFormat: 'brackets',
          encode: true
        })
        window.open(`${this.ws}/config_limite_saldo_devedor/exportar?${encoded_params}`)
      },

      resetForm () {
        this.configuracao = {
          id: null,
          limite_saldo_devedor: null,
          tipo_grupo: []
        }
        this.$refs.form.resetValidation()
      },

      toggleFiltro () {
        this.filter_open = !this.filter_open
      },

      filtrar () {
        const filtros = { ...this.filters }
        this.dtFilters = filtros
      },

      limpar () {
        this.filters = {
          limite_saldo_devedor: null,
          tipo_grupo: []
        }
        this.filtrar()
        this.filter_open = false
      }
    }
  }
</script>
